/*
 * BDEF-CUSTOM: búsqueda predictiva v6.
 * - Mapa de términos en metaobjetos (bdef_search_term, bdef_search_shortcut), servido por
 *   sections/bdef-search-map.liquid y cargado la primera vez que se abre el buscador.
 * - Una petición Storefront GraphQL con alias: capa exact (sintaxis de campo), recuentos de chips
 *   relacionados, capa broad (búsqueda natural con prefix LAST) y colecciones.
 * - Se registra antes que theme.js: Prestige solo define <predictive-search> si no existe.
 * Versión anterior guardada en assets/bdef-predictive-search-v5.js.
 */

const CFG = {
  API: '/api/2026-10/graphql.json',
  TOKEN: 'bff0936d5e0922aadc15749ef399fa81',
  MAP_SECTION: 'bdef-search-map',
  MAP_CACHE_KEY: 'bdef-search-map-v1',
  MAP_TTL_MS: 10 * 60 * 1000,
  EXCLUDE_TAG: 'Ocultos OOS',
  EXACT_LIMIT: 18,
  BROAD_LIMIT: 6,
  MAX_CHIPS: 4,
  COLLECTION_LIMIT: 4,
  MIN_CHARS: 2,
  DEBOUNCE_MS: 250,
  TRACK_IDLE_MS: 1200,
  NEW_DAYS: Number(window.bdefConfig?.newBadgeDays) || 60,
  CURRENCY: window.bdefConfig?.currency || 'EUR',
  // Colecciones técnicas que nunca se sugieren en el buscador
  HIDDEN_COLLECTIONS: /^(all|orderlyemails-.*|todos-productos-.*|articulos-de-continuidad|no-rebajas-.*|black-friday|oi\d\d|pv\d\d)$/,
  GENDER_LABEL: { mujer: 'mujer', hombre: 'hombre', kids: 'kids' }
};

// ─── NORMALIZACIÓN Y ESCAPE ─────────────────────────────────────────────────

/** Minúsculas, sin tildes, sin puntos ni apóstrofos, & aislado, solo [a-z0-9&] y espacios. */
function norm(s) {
  return String(s || '')
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[.'´`’]/g, '')
    .replace(/&/g, ' & ')
    .replace(/[^a-z0-9&]+/g, ' ')
    .trim();
}

function esc(s) {
  return String(s ?? '').replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
}

const quote = v => '"' + String(v).replace(/\\/g, '\\\\').replace(/"/g, '\\"') + '"';

// Lista blanca de campos: es la única vía para construir consultas con sintaxis de campo.
const FIELD = {
  type: v => 'product_type:' + quote(v),
  title: w => { const x = norm(w).replace(/[^a-z0-9]/g, ''); return x ? 'title:' + x : null; },
  vendor: v => 'vendor:' + quote(v),
  tag: v => 'tag:' + quote(v)
};

const anyOf = arr => {
  const a = arr.filter(Boolean);
  if (!a.length) return null;
  return a.length === 1 ? a[0] : '(' + a.join(' OR ') + ')';
};

// ─── MAPA ───────────────────────────────────────────────────────────────────

const EMPTY_MAP = { terms: new Map(), index: new Map(), prefixes: new Set(), fuzzy: [], maxN: 1, stop: new Set(), shortcuts: [], trending: [], ready: false };

function buildMap(raw) {
  const terms = new Map();
  const index = new Map();
  const stop = new Set();
  let maxN = 1;

  const addAlias = (alias, entry) => {
    const a = norm(alias);
    if (!a) return;
    const prev = index.get(a);
    if (prev && prev !== entry && !entry.auto) console.warn(`[bdef-search] Alias "${a}" repetido en "${prev.h}" y "${entry.h}"`);
    if (prev && entry.auto) return; // las marcas automáticas nunca pisan el mapa
    index.set(a, entry);
    maxN = Math.max(maxN, a.split(' ').length);
  };

  for (const t of raw.terms || []) {
    if (!t || !t.h) continue;
    const e = {
      h: t.h,
      label: t.l || t.h,
      kind: t.k,
      types: t.pt || [],
      titles: t.tw || [],
      mode: t.m || ((t.pt || []).length ? 'tipo' : 'titulo'),
      vendors: t.v || [],
      tags: t.tg || [],
      related: t.r || [],
      cols: t.c || {}
    };
    terms.set(e.h, e);
    if (e.kind === 'stopword') { (t.a || []).forEach(a => stop.add(norm(a))); continue; }
    (t.a || []).forEach(a => addAlias(a, e));
  }

  // Marcas del catálogo (shop.vendors): se reconocen sin mantenerlas a mano.
  for (const v of raw.vendors || []) {
    if (!v || !v.n) continue;
    addAlias(v.n, { h: 'vendor:' + norm(v.n), label: v.n, kind: 'marca', auto: true, types: [], titles: [], vendors: [v.n], tags: [], related: [], cols: v.u ? { general: v.u } : {} });
  }

  // Validación de tipos de producto contra el catálogo (solo consola).
  const known = new Set(raw.types || []);
  if (known.size) {
    for (const e of terms.values()) e.types.forEach(tp => { if (!known.has(tp)) console.warn(`[bdef-search] "${e.h}": tipo "${tp}" no existe en la tienda`); });
  }

  const shortcuts = (raw.shortcuts || []).filter(s => s && s.u && s.d)
    .map(s => ({ label: s.l, url: s.u, order: Number(s.o) || 99, triggers: s.d.map(norm).filter(Boolean) }))
    .sort((a, b) => a.order - b.order);

  // Prefijos (4+ letras) de todos los alias y marcas: un atajo nunca se activa por prefijo
  // si lo escrito puede ser el comienzo de un término de producto.
  const prefixes = new Set();
  for (const a of index.keys()) for (let i = 4; i <= a.length; i++) prefixes.add(a.slice(0, i));

  // Alias (5+ letras) candidatos a corregir erratas en términos de producto, con su número de palabras.
  const fuzzy = [];
  for (const [a, e] of index) if (a.length >= 5) fuzzy.push([a, e, a.split(' ').length]);

  return { terms, index, prefixes, fuzzy, maxN, stop, shortcuts, trending: (raw.trending || []).filter(t => t && t.u), ready: true };
}

let _mapPromise = null;

function loadMap() {
  if (_mapPromise) return _mapPromise;
  _mapPromise = (async () => {
    try {
      const cached = JSON.parse(sessionStorage.getItem(CFG.MAP_CACHE_KEY) || 'null');
      if (cached && Date.now() - cached.t < CFG.MAP_TTL_MS) return buildMap(cached.d);
    } catch (e) { /* sessionStorage no disponible */ }
    try {
      const root = window.Shopify?.routes?.root || '/';
      const res = await fetch(`${root}?sections=${CFG.MAP_SECTION}`, { credentials: 'same-origin' });
      if (!res.ok) throw new Error('HTTP ' + res.status);
      const html = (await res.json())[CFG.MAP_SECTION] || '';
      const tpl = document.createElement('template');
      tpl.innerHTML = html;
      const node = tpl.content.querySelector('#bdef-search-map');
      if (!node) throw new Error('mapa no encontrado');
      const data = JSON.parse(node.textContent);
      try { sessionStorage.setItem(CFG.MAP_CACHE_KEY, JSON.stringify({ t: Date.now(), d: data })); } catch (e) { /* cuota */ }
      return buildMap(data);
    } catch (e) {
      console.warn('[bdef-search] Sin mapa, solo búsqueda natural:', e);
      _mapPromise = null; // reintenta en la siguiente búsqueda
      return EMPTY_MAP;
    }
  })();
  return _mapPromise;
}

// ─── ATAJOS: palabra completa, erratas y prefijo ─────────────────────────────

/** Distancia de edición con transposición (Damerau, variante OSA). */
function editDistance(a, b) {
  const m = a.length, n = b.length;
  if (Math.abs(m - n) > 2) return 3;
  const d = Array.from({ length: m + 1 }, (_, i) => [i, ...Array(n).fill(0)]);
  for (let j = 1; j <= n; j++) d[0][j] = j;
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      d[i][j] = Math.min(d[i - 1][j] + 1, d[i][j - 1] + 1, d[i - 1][j - 1] + cost);
      if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) d[i][j] = Math.min(d[i][j], d[i - 2][j - 2] + 1);
    }
  }
  return d[m][n];
}

function matchShortcut(text, tokens, map) {
  if (!map.shortcuts.length || !tokens.length) return null;
  const padded = ' ' + text + ' ';

  // 1. Palabra o frase completa: "devolucion", "mi pedido".
  for (const s of map.shortcuts) {
    if (s.triggers.some(t => padded.includes(' ' + t + ' '))) return s;
  }

  // 2. Erratas en palabras completas: "devulucion", "devocion", "whatsap".
  //    Misma primera letra, 5+ letras, y la palabra no es un término del mapa ni una marca.
  for (const tk of tokens) {
    if (tk.length < 5 || /\d/.test(tk) || map.index.has(tk)) continue;
    for (const s of map.shortcuts) {
      for (const t of s.triggers) {
        if (t.includes(' ') || t[0] !== tk[0]) continue;
        const max = t.length >= 8 ? 2 : 1;
        if (editDistance(tk, t) <= max) return s;
      }
    }
  }

  // 3. Prefijo de la última palabra mientras se escribe: "devo", "camb", "whats".
  //    Solo si lo escrito no puede ser el comienzo de un término de producto o una marca.
  const last = tokens[tokens.length - 1];
  if (last.length >= 4 && !/\d/.test(last) && !map.prefixes.has(last)) {
    for (const s of map.shortcuts) {
      if (s.triggers.some(t => !t.includes(' ') && t.startsWith(last))) return s;
    }
  }
  return null;
}

/**
 * Corrige una errata en una palabra completa contra los alias del mapa. Reglas estrictas:
 * 4+ letras, misma primera letra, 1 error (2 si el alias tiene 9+ letras), la palabra no puede
 * ser el comienzo de un alias (el cliente aún está escribiendo) y el mejor candidato debe ser único.
 * Con 4 letras solo se admite la letra que falta ("mujr" → "mujer"), nunca una sustitución.
 */
function fuzzyTerm(tk, map, words = 1) {
  if (tk.length < 4 || /\d/.test(tk) || map.prefixes.has(tk) || map.index.has(tk)) return null;
  let best = 3, hits = new Map();
  for (const [a, e, n] of map.fuzzy) {
    if (n !== words || a[0] !== tk[0] || Math.abs(a.length - tk.length) > 2) continue;
    if (tk.length === 4 && a.length !== 5) continue;
    const max = a.length >= 9 ? 2 : 1;
    const d = editDistance(tk, a);
    if (d > max || d > best) continue;
    if (d < best) { best = d; hits = new Map(); }
    hits.set(e.h, { e, a });
  }
  return hits.size === 1 ? [...hits.values()][0] : null;
}

// ─── PARSER ─────────────────────────────────────────────────────────────────

function parse(raw, map) {
  const text = norm(raw);
  const tokens = text ? text.split(' ') : [];
  const p = { raw, text, tipo: [], estilo: [], color: [], marca: [], genero: null, intencion: null, rest: [], code: null, broadTokens: [], shortcut: null, corrections: [] };

  p.shortcut = matchShortcut(text, tokens, map);

  for (let i = 0; i < tokens.length;) {
    let hit = null, len = 0;
    for (let k = Math.min(map.maxN, tokens.length - i); k >= 1; k--) {
      const phrase = tokens.slice(i, i + k).join(' ');
      if (map.index.has(phrase)) { hit = map.index.get(phrase); len = k; break; }
    }
    if (hit) {
      const consumed = tokens.slice(i, i + len);
      i += len;
      if (hit.kind === 'genero') { p.genero = hit; continue; }
      if (hit.kind === 'intencion') { p.intencion = hit; continue; }
      if (p[hit.kind] && !p[hit.kind].includes(hit)) p[hit.kind].push(hit);
      p.broadTokens.push(...consumed);
      continue;
    }
    // Errata: primero frases de varias palabras ("salsa jeens"), luego la palabra suelta.
    let fz = null, fzLen = 0;
    if (!p.shortcut) {
      for (let k = Math.min(map.maxN, tokens.length - i); k >= 1 && !fz; k--) {
        if (k > 1 && tokens.slice(i, i + k).some(t => map.stop.has(t))) continue;
        fz = fuzzyTerm(tokens.slice(i, i + k).join(' '), map, k);
        if (fz) fzLen = k;
      }
    }
    if (fz) {
      i += fzLen;
      const tk = tokens.slice(i - fzLen, i).join(' ');
      const e = fz.e;
      p.corrections.push({ from: tk, to: fz.a, label: e.label });
      if (e.kind === 'genero') p.genero = e;
      else if (e.kind === 'intencion') p.intencion = e;
      else if (p[e.kind] && !p[e.kind].includes(e)) p[e.kind].push(e);
      if (e.kind !== 'genero' && e.kind !== 'intencion') p.broadTokens.push(fz.a);
      continue;
    }
    const tk = tokens[i++];
    if (tk === '&' || map.stop.has(tk)) continue;
    if (/^\d{1,3}$/.test(tk)) continue;                         // tallas: "42", "xl" no llega aquí
    if (/\d/.test(tk) && tk.length >= 5) { p.code = tk; p.broadTokens.push(tk); continue; } // SKU o referencia
    p.broadTokens.push(tk);
    if (tk.length >= 2) p.rest.push(tk);
  }
  return p;
}

// ─── COMPILADOR ─────────────────────────────────────────────────────────────

function termClause(t) {
  const T = anyOf(t.types.map(FIELD.type));
  const W = anyOf(t.titles.map(FIELD.title));
  switch (t.mode) {
    case 'tipo': return T || W;
    case 'titulo': return W || T;
    case 'tipo_y_titulo': return T && W ? `(${T} AND ${W})` : (T || W);
    case 'tipo_o_titulo': return anyOf(t.types.map(FIELD.type).concat(t.titles.map(FIELD.title)));
    default: return T || W;
  }
}

function filterClauses(p) {
  const out = [];
  if (p.genero && p.genero.tags.length) out.push(anyOf(p.genero.tags.map(FIELD.tag)));
  if (p.intencion && p.intencion.tags.length) out.push(anyOf(p.intencion.tags.map(FIELD.tag)));
  out.push('NOT ' + FIELD.tag(CFG.EXCLUDE_TAG));
  return out;
}

/** Consulta de la capa exact para un término principal dado (o ninguno). */
function compile(p, primary) {
  const core = [];
  if (primary) core.push(termClause(primary));
  p.estilo.forEach(t => core.push(termClause(t)));
  p.color.forEach(t => core.push(termClause(t)));
  if (p.marca.length) core.push(anyOf(p.marca.flatMap(t => t.vendors).map(FIELD.vendor)));
  p.rest.forEach(w => core.push(FIELD.title(w)));
  const clean = core.filter(Boolean);
  if (!clean.length && !p.intencion) return null;
  return clean.concat(filterClauses(p)).join(' AND ');
}

function planSearch(p, map) {
  const chips = [];
  const primary = p.tipo[0] || null;
  if (primary) {
    const seen = new Set();
    const add = t => { if (t && !seen.has(t.h) && chips.length < CFG.MAX_CHIPS) { seen.add(t.h); chips.push({ term: t, query: compile(p, t) }); } };
    add(primary);
    p.tipo.slice(1).forEach(add);
    primary.related.forEach(h => add(map.terms.get(h)));
  }
  const exact = primary ? chips[0].query : (p.code ? null : compile(p, null));
  const broadText = p.broadTokens.join(' ').trim();

  // Novedades por fecha de creación (misma ventana que la insignia): la búsqueda de Shopify
  // ignora los filtros de fecha, así que este modo usa la consulta products con su propio filtro.
  if (p.intencion && !p.intencion.tags.length) {
    return { novelty: true, primary, chips: primary ? [{ term: primary, query: null }] : [], exact: null, broad: null, colq: null };
  }
  return {
    chips,
    exact,
    broad: broadText.length >= CFG.MIN_CHARS ? broadText : null,
    colq: broadText.length >= 3 ? broadText : null
  };
}

// ─── NOVEDADES POR FECHA ─────────────────────────────────────────────────────

/** Parte que filtra el servidor: fecha, stock, exclusión, género, marca y tipo cuando el modo lo permite. */
function noveltyQuery(p, primary) {
  const since = new Date(Date.now() - CFG.NEW_DAYS * 86400000).toISOString().slice(0, 10);
  const parts = [`created_at:>'${since}'`, 'available_for_sale:true', 'NOT ' + FIELD.tag(CFG.EXCLUDE_TAG)];
  if (p.genero && p.genero.tags.length) parts.push(anyOf(p.genero.tags.map(FIELD.tag)));
  if (p.marca.length) parts.push(anyOf(p.marca.flatMap(t => t.vendors).map(FIELD.vendor)));
  if (primary && primary.types.length && (primary.mode === 'tipo' || primary.mode === 'tipo_y_titulo')) parts.push(anyOf(primary.types.map(FIELD.type)));
  return parts.join(' AND ');
}

/** Parte que se filtra en el navegador: el filtro title: no funciona en la consulta products. */
function titleWords(n) {
  return new Set(norm(n.title).split(' '));
}
function matchTermLocal(n, words, t) {
  const byType = t.types.includes(n.productType);
  const byTitle = t.titles.some(w => words.has(norm(w)));
  switch (t.mode) {
    case 'tipo': return t.types.length ? byType : byTitle;
    case 'titulo': return t.titles.length ? byTitle : byType;
    case 'tipo_y_titulo': return (t.types.length ? byType : true) && (t.titles.length ? byTitle : true);
    case 'tipo_o_titulo': return byType || byTitle;
    default: return byType || byTitle;
  }
}
function matchNoveltyLocal(n, p, primary) {
  const words = titleWords(n);
  if (primary && !matchTermLocal(n, words, primary)) return false;
  if (!p.estilo.every(t => matchTermLocal(n, words, t))) return false;
  if (!p.color.every(t => matchTermLocal(n, words, t))) return false;
  return p.rest.every(w => [...words].some(x => x === w || x.startsWith(w)));
}

// ─── GRAPHQL ────────────────────────────────────────────────────────────────

const PRODUCT_FIELDS = 'id handle title vendor productType createdAt trackingParameters featuredImage { url(transform: { maxWidth: 400 }) altText } priceRange { minVariantPrice { amount currencyCode } } compareAtPriceRange { minVariantPrice { amount currencyCode } }';

function buildRequest(plan) {
  const defs = [], body = [], vars = {};
  if (plan.exact) {
    defs.push('$qe: String!'); vars.qe = plan.exact;
    body.push(`exact: search(query: $qe, first: ${CFG.EXACT_LIMIT}, types: PRODUCT, unavailableProducts: HIDE) { totalCount nodes { ... on Product { ${PRODUCT_FIELDS} } } }`);
  }
  plan.chips.forEach((c, i) => {
    if (i === 0 || !c.query) return;
    defs.push(`$q${i}: String!`); vars['q' + i] = c.query;
    body.push(`c${i}: search(query: $q${i}, first: 1, types: PRODUCT, unavailableProducts: HIDE) { totalCount }`);
  });
  if (plan.broad) {
    defs.push('$qb: String!'); vars.qb = plan.broad;
    body.push(`broad: search(query: $qb, first: ${CFG.BROAD_LIMIT + 6}, types: PRODUCT, prefix: LAST, unavailableProducts: HIDE) { nodes { ... on Product { tags ${PRODUCT_FIELDS} } } }`);
  }
  if (plan.colq) {
    defs.push('$qc: String!'); vars.qc = plan.colq;
    body.push(`cols: predictiveSearch(query: $qc, limit: ${CFG.COLLECTION_LIMIT + 4}, types: [COLLECTION]) { collections { handle title trackingParameters } }`);
  }
  if (!body.length) return null;
  return { query: `query BdefSearch(${defs.join(', ')}) { ${body.join(' ')} }`, variables: vars };
}

async function gql(req, signal) {
  const res = await fetch(CFG.API, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'X-Shopify-Storefront-Access-Token': CFG.TOKEN },
    body: JSON.stringify(req),
    signal
  });
  if (!res.ok) throw new Error('Storefront HTTP ' + res.status);
  const json = await res.json();
  if (json.errors) console.warn('[bdef-search] GraphQL:', json.errors);
  return json.data || {};
}

// ─── ESTILOS ────────────────────────────────────────────────────────────────

let _styles = false;
function injectStyles() {
  if (_styles) return;
  _styles = true;
  const css = `
.bdef-ps{width:100%;max-height:70vh;overflow-y:auto;-webkit-overflow-scrolling:touch;padding-bottom:24px;background:#FBFBFB}
@media(min-width:750px){.bdef-ps{max-height:none;overflow:visible}}
.bdef-ps :focus-visible{outline:2px solid #2c2c2c;outline-offset:2px}
.bdef-ps__banner{display:flex;align-items:center;gap:12px;margin:10px;padding:12px 14px;background:#fff;border:1px solid #e1e1e1;border-radius:6px;color:#333;text-decoration:none;font-size:13px;font-weight:600}
.bdef-ps__banner small{display:block;font-size:11px;font-weight:400;color:#666}
.bdef-ps__banner svg{margin-left:auto;flex:none;color:#999}
.bdef-ps__chips{display:flex;gap:8px;overflow-x:auto;padding:12px 10px 4px;scrollbar-width:none}
.bdef-ps__chips::-webkit-scrollbar{display:none}
.bdef-ps__chip{flex:none;display:inline-flex;align-items:baseline;gap:6px;padding:8px 14px;border:1px solid #d6d6d6;border-radius:999px;background:#fff;color:#333;font:inherit;font-size:12px;font-weight:600;cursor:pointer}
.bdef-ps__chip span{font-weight:400;color:#777}
.bdef-ps__chip[aria-pressed="true"]{background:#2c2c2c;border-color:#2c2c2c;color:#fff}
.bdef-ps__chip[aria-pressed="true"] span{color:#ccc}
.bdef-ps__grid{display:grid;grid-template-columns:repeat(3,1fr);column-gap:5px;row-gap:15px;padding:12px 10px;margin:0}
@media(min-width:750px){.bdef-ps__grid{grid-template-columns:repeat(6,1fr);column-gap:10px;row-gap:20px;padding:15px}}
.bdef-ps__grid[aria-busy="true"]{opacity:.45}
.bdef-ps__card{display:block;position:relative;color:inherit;text-decoration:none;font-size:12px;text-transform:uppercase}
.bdef-ps__img{position:relative;aspect-ratio:3/4;margin-bottom:4px;overflow:hidden;border-radius:4px;background:#f4f4f4}
.bdef-ps__img img{width:100%;height:100%;object-fit:cover;display:block}
.bdef-ps__badges{position:absolute;top:5px;left:5px;display:flex;flex-direction:column;gap:3px;pointer-events:none}
.bdef-ps__badge{font-size:10px;font-weight:600;line-height:1;padding:2px 4px;border-radius:2px;color:#fff;background:#2c2c2c}
.bdef-ps__badge--sale{background:#c91c1c}
.bdef-ps__title{display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden;height:2.4em;line-height:1.2;font-weight:500}
.bdef-ps__price{display:flex;flex-wrap:wrap;gap:4px;align-items:center;color:#666}
.bdef-ps__price--sale{color:#c91c1c;font-weight:600}
.bdef-ps__price s{color:#999;font-size:11px}
.bdef-ps__more{display:flex;flex-wrap:wrap;gap:10px 18px;justify-content:center;padding:15px;border-top:1px solid #eee}
.bdef-ps__more a{font-size:12px;font-weight:600;color:inherit;text-decoration:underline;text-underline-offset:3px;text-transform:uppercase}
.bdef-ps__section{padding:14px 10px 0;font-size:12px;font-weight:600;color:#555}
.bdef-ps__cols{display:flex;flex-wrap:wrap;gap:8px;padding:8px 10px 0}
.bdef-ps__cols a{padding:7px 12px;border:1px solid #e1e1e1;border-radius:4px;background:#fff;color:#333;text-decoration:none;font-size:12px}
.bdef-ps__empty{padding:22px 15px 8px;text-align:center;color:#555;font-size:13px;line-height:1.5}
.bdef-ps__trend{display:flex;flex-wrap:wrap;gap:8px;justify-content:center;padding:12px 15px 0}
.bdef-ps__trend a{padding:8px 12px;border:1px solid #e1e1e1;border-radius:4px;background:#fff;color:#333;text-decoration:none;font-size:12px;font-weight:600}
@media(hover:hover){.bdef-ps__banner:hover,.bdef-ps__cols a:hover,.bdef-ps__trend a:hover{background:#f0f0f0}}
`;
  const style = document.createElement('style');
  style.id = 'bdef-predictive-search-styles';
  style.textContent = css;
  document.head.appendChild(style);
}

const CHEVRON = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="9 18 15 12 9 6"></polyline></svg>';

// ─── COMPONENTE ─────────────────────────────────────────────────────────────

class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.cache = new Map();
    this.abortController = null;
    this.state = null;
    this.trackTimer = null;
    this.lastTracked = '';
    this.attachShadow({ mode: 'open' }).innerHTML = '<slot name="results"></slot>';
  }

  connectedCallback() {
    this.form = document.querySelector(`[aria-owns="${this.id}"]`) || this.closest('form');
    if (!this.form) return;
    this.input = this.form.elements.q;
    this.slotEl = this.querySelector('[slot="results"]');
    if (!this.input || !this.slotEl) return;

    const warm = () => loadMap();
    this.input.addEventListener('focus', warm, { once: true });

    this.input.addEventListener('input', this.debounce(() => this.onInput(), CFG.DEBOUNCE_MS));

    this.form.addEventListener('submit', e => {
      const v = this.input.value.trim();
      if (!v) { e.preventDefault(); return; }
      const dest = this.state?.term === v ? this.state.viewAll : null;
      if (dest && dest.length === 1) {
        e.preventDefault();
        this.track('view_search_results', { search_term: v, results_shown: this.state.total, transport_type: 'beacon' });
        window.location.href = dest[0].url;
      }
    });

    this.form.addEventListener('reset', () => { this.abortController?.abort(); this.clear(); });

    this.slotEl.addEventListener('click', e => this.onClick(e));
  }

  clear() {
    this.state = null;
    this.slotEl.innerHTML = '';
  }

  debounce(fn, wait) {
    let t;
    return () => { clearTimeout(t); t = setTimeout(fn, wait); };
  }

  // ── Flujo principal ───────────────────────────────────────────────────────

  async onInput() {
    const term = this.input.value.trim();
    if (term.length < CFG.MIN_CHARS) { this.abortController?.abort(); this.clear(); return; }

    this.abortController?.abort();
    this.abortController = new AbortController();
    const signal = this.abortController.signal;

    document.documentElement.dispatchEvent(new CustomEvent('theme:loading:start', { bubbles: true }));
    try {
      const map = await loadMap();
      this._trending = map.trending;
      const p = parse(term, map);
      const key = p.text;
      let result = this.cache.get(key);
      if (!result) {
        result = await this.search(p, map, signal);
        if (signal.aborted) return;
        this.cache.set(key, result);
      }
      if (this.input.value.trim() !== term) return;
      this.render(term, p, result);
    } catch (e) {
      if (e.name !== 'AbortError') {
        console.error('[bdef-search]', e);
        this.renderError(term);
      }
    } finally {
      document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));
    }
  }

  async search(p, map, signal) {
    const plan = planSearch(p, map);
    if (plan.novelty) return this.searchNovelty(p, plan, signal);
    const req = buildRequest(plan);
    const data = req ? await gql(req, signal) : {};

    const exactNodes = (data.exact?.nodes || []).filter(n => n.featuredImage);
    const exactTotal = data.exact?.totalCount || 0;

    const chips = plan.chips.map((c, i) => ({
      term: c.term,
      query: c.query,
      count: i === 0 ? exactTotal : (data['c' + i]?.totalCount || 0),
      nodes: i === 0 ? exactNodes : null
    })).filter((c, i) => i === 0 || c.count > 0);

    const genderTags = p.genero ? p.genero.tags.map(t => t.toLowerCase()) : null;
    const seen = new Set(exactNodes.map(n => n.handle));
    const broadNodes = (data.broad?.nodes || []).filter(n => {
      if (!n.featuredImage || seen.has(n.handle)) return false;
      const tags = (n.tags || []).map(t => t.toLowerCase());
      if (tags.includes(CFG.EXCLUDE_TAG.toLowerCase())) return false;
      if (genderTags && !tags.some(t => genderTags.includes(t))) return false;
      return true;
    }).slice(0, CFG.BROAD_LIMIT);

    const collections = (data.cols?.collections || []).filter(c => !CFG.HIDDEN_COLLECTIONS.test(c.handle)).slice(0, CFG.COLLECTION_LIMIT);

    return { plan, chips, exactNodes, exactTotal, broadNodes, collections };
  }

  async searchNovelty(p, plan, signal) {
    const q = noveltyQuery(p, plan.primary);
    const pr = plan.primary;
    // Solo se descarga en volumen cuando hay que filtrar por título en el navegador.
    const needsLocal = !!(p.estilo.length || p.color.length || p.rest.length || (pr && (pr.mode === 'titulo' || pr.mode === 'tipo_o_titulo' || (pr.mode === 'tipo_y_titulo' && pr.titles.length))));
    const size = needsLocal ? 250 : 100;
    const Q = `query BdefNew($q: String!, $a: String) { products(first: ${size}, after: $a, query: $q, sortKey: CREATED_AT, reverse: true) { pageInfo { hasNextPage endCursor } nodes { ${PRODUCT_FIELDS} } } }`;
    let after = null, all = [], capped = false;
    for (let page = 0; page < (needsLocal ? 2 : 1); page++) {
      const data = await gql({ query: Q, variables: { q, a: after } }, signal);
      const conn = data.products;
      if (!conn) break;
      all = all.concat(conn.nodes);
      capped = conn.pageInfo.hasNextPage;
      if (!capped) break;
      after = conn.pageInfo.endCursor;
    }
    let nodes = all.filter(n => n.featuredImage && matchNoveltyLocal(n, p, pr));
    // Si una palabra no reconocida deja la búsqueda vacía, se ignora y se avisa de que es aproximada.
    let approx = false;
    if (!nodes.length && p.rest.length) {
      const loose = { ...p, rest: [] };
      nodes = all.filter(n => n.featuredImage && matchNoveltyLocal(n, loose, pr));
      approx = nodes.length > 0;
    }
    const total = capped ? `${nodes.length}+` : nodes.length;
    const chips = pr ? [{ term: pr, query: null, count: total, nodes: nodes.slice(0, CFG.EXACT_LIMIT) }] : [];
    return { plan, chips, exactNodes: nodes.slice(0, CFG.EXACT_LIMIT), exactTotal: total, broadNodes: [], collections: [], approx };
  }

  // ── Destino de "Ver todos" ────────────────────────────────────────────────

  viewAllFor(p, chipTerm) {
    const g = p.genero?.h;
    const G = CFG.GENDER_LABEL;
    const one = (url, label) => [{ url, label }];
    const lc = s => String(s).toLowerCase();

    if (p.intencion && !p.intencion.tags.length) {
      const c = p.intencion.cols;
      if (g && c[g]) return one(c[g], `Ver ${lc(p.intencion.label)} ${G[g]}`);
      const split = ['mujer', 'hombre', 'kids'].filter(k => c[k]);
      if (split.length) return split.map(k => ({ url: c[k], label: `${p.intencion.label} ${G[k]}` }));
    }
    if (chipTerm && g && chipTerm.cols[g]) return one(chipTerm.cols[g], `Ver ${lc(chipTerm.label)} ${G[g]}`);
    const marca = p.marca[0];
    if (marca?.cols?.general && (!chipTerm || !g)) return one(marca.cols.general, `Ver todo ${marca.label}`);
    if (chipTerm?.cols?.general) return one(chipTerm.cols.general, `Ver ${lc(chipTerm.label)}`);

    if (!chipTerm && p.intencion) {
      const c = p.intencion.cols;
      if (g && c[g]) return one(c[g], `Ver ${lc(p.intencion.label)} ${G[g]}`);
      const split = ['mujer', 'hombre', 'kids'].filter(k => c[k]);
      if (split.length) return split.map(k => ({ url: c[k], label: `${p.intencion.label} ${G[k]}` }));
    }

    if (chipTerm) {
      const split = ['mujer', 'hombre', 'kids'].filter(k => chipTerm.cols[k]);
      if (split.length === 1) return one(chipTerm.cols[split[0]], `Ver ${lc(chipTerm.label)}`);
      if (split.length > 1) return split.map(k => ({ url: chipTerm.cols[k], label: `${chipTerm.label} ${G[k]}` }));
    }

    if (!chipTerm && !p.marca.length && !p.rest.length && p.genero?.cols?.general) return one(p.genero.cols.general, `Ver moda ${G[g] || ''}`.trim());

    const q = p.broadTokens.join(' ') || p.text;
    return one(`${window.Shopify?.routes?.root || '/'}search?q=${encodeURIComponent(q)}&type=product&options%5Bprefix%5D=last`, 'Ver todos los resultados');
  }

  // ── Render ────────────────────────────────────────────────────────────────

  render(term, p, r) {
    injectStyles();
    const activeChip = r.chips[0] || null;
    const hasExact = r.exactNodes.length > 0;
    const nodes = hasExact ? r.exactNodes : r.broadNodes;
    const total = hasExact ? r.exactTotal : r.broadNodes.length;
    const viewAll = this.viewAllFor(p, activeChip?.term || null);

    this.state = { term, p, r, active: 0, viewAll, total };

    let html = '';
    if (p.shortcut) html += this.bannerHtml(p.shortcut.url, p.shortcut.label, 'Ir ahora', 'shortcut');

    const genderOnly = !nodes.length && p.genero && !p.tipo.length && !p.marca.length && !p.rest.length;

    if (nodes.length) {
      if (p.corrections.length && hasExact) html += `<p class="bdef-ps__section">Resultados para «${esc(p.corrections.map(c => c.label).join(', '))}»</p>`;
      if (r.approx) html += '<p class="bdef-ps__section">Resultados aproximados</p>';
      if (r.chips.length > 1 && hasExact) html += this.chipsHtml(r.chips, 0);
      html += `<div class="bdef-ps__grid" data-grid>${this.cardsHtml(nodes, term)}</div>`;
      if (!hasExact && r.exactTotal === 0 && r.plan.exact) html = html.replace('<div class="bdef-ps__grid"', '<p class="bdef-ps__section">Resultados aproximados</p><div class="bdef-ps__grid"');
      html += this.viewAllHtml(viewAll);
      if (r.collections.length) {
        html += '<p class="bdef-ps__section">Colecciones</p><div class="bdef-ps__cols">' +
          r.collections.map(c => `<a href="/collections/${esc(c.handle)}${c.trackingParameters ? '?' + esc(c.trackingParameters) : ''}" data-collection="${esc(c.handle)}">${esc(c.title)}</a>`).join('') + '</div>';
      }
    } else if (genderOnly) {
      html += viewAll.map(v => this.bannerHtml(v.url, v.label, 'Ver colección', 'gender')).join('');
      html += this.trendingHtml('Lo más buscado');
    } else if (!p.shortcut) {
      html += `<p class="bdef-ps__empty">No hay resultados para «${esc(term)}».<br>Prueba con otra palabra o con una de estas búsquedas.</p>`;
      html += this.trendingHtml('');
    } else {
      html += this.trendingHtml('');
    }

    this.slotEl.innerHTML = `<div class="bdef-ps" role="region" aria-label="Resultados de búsqueda">${html}</div>`;
    this.announce(nodes.length ? `${total} productos` : 'Sin resultados');
    this.scheduleTrack(term, p, total, hasExact ? 'exact' : (nodes.length ? 'broad' : (p.shortcut ? 'shortcut' : 'none')));
  }

  renderError(term) {
    injectStyles();
    this.slotEl.innerHTML = `<div class="bdef-ps"><p class="bdef-ps__empty">La búsqueda no ha respondido. Pulsa Intro para ver los resultados de «${esc(term)}».</p></div>`;
  }

  chipsHtml(chips, active) {
    return '<div class="bdef-ps__chips" role="group" aria-label="Filtrar por tipo">' +
      chips.map((c, i) => `<button type="button" class="bdef-ps__chip" data-chip="${i}" aria-pressed="${i === active}">${esc(c.term.label)}<span>${c.count}</span></button>`).join('') +
      '</div>';
  }

  cardsHtml(nodes, term) {
    const now = Date.now();
    return nodes.slice(0, CFG.EXACT_LIMIT).map((n, idx) => {
      const price = parseFloat(n.priceRange.minVariantPrice.amount);
      const cur = n.priceRange.minVariantPrice.currencyCode || CFG.CURRENCY;
      const compare = parseFloat(n.compareAtPriceRange?.minVariantPrice?.amount || 0);
      let badges = '';
      let priceHtml = `<span>${this.money(price, cur)}</span>`;
      if (compare > price) {
        badges += `<span class="bdef-ps__badge bdef-ps__badge--sale">-${Math.round((compare - price) / compare * 100)}%</span>`;
        priceHtml = `<span class="bdef-ps__price--sale">${this.money(price, cur)}</span><s>${this.money(compare, cur)}</s>`;
      }
      if (n.createdAt && (now - new Date(n.createdAt)) / 86400000 < CFG.NEW_DAYS) badges += '<span class="bdef-ps__badge">Novedad</span>';
      const url = `/products/${n.handle}${n.trackingParameters ? '?' + n.trackingParameters : ''}`;
      return `<a href="${esc(url)}" class="bdef-ps__card" data-pos="${idx + 1}" data-handle="${esc(n.handle)}">
<div class="bdef-ps__img"><div class="bdef-ps__badges">${badges}</div><img src="${esc(n.featuredImage.url)}" alt="${esc(n.featuredImage.altText || n.title)}" loading="lazy" width="400" height="533"></div>
<span class="bdef-ps__title">${esc(n.title)}</span><div class="bdef-ps__price">${priceHtml}</div></a>`;
    }).join('');
  }

  viewAllHtml(list) {
    return '<div class="bdef-ps__more">' + list.map(v => `<a href="${esc(v.url)}" data-viewall>${esc(v.label)}</a>`).join('') + '</div>';
  }

  bannerHtml(url, label, sub, kind) {
    return `<a href="${esc(url)}" class="bdef-ps__banner" data-banner="${kind}"><span>${esc(label)}<small>${esc(sub)}</small></span>${CHEVRON}</a>`;
  }

  trendingHtml(title) {
    const list = this.mapTrending();
    if (!list.length) return '';
    return (title ? `<p class="bdef-ps__section" style="text-align:center">${esc(title)}</p>` : '') +
      '<div class="bdef-ps__trend">' + list.map(t => `<a href="${esc(t.u)}" data-trend>${esc(t.l)}</a>`).join('') + '</div>';
  }

  mapTrending() {
    return this._trending || [];
  }

  money(amount, currency) {
    try { return new Intl.NumberFormat('es-ES', { style: 'currency', currency }).format(amount || 0); }
    catch (e) { return (amount || 0).toFixed(2) + ' ' + currency; }
  }

  announce(text) {
    const el = document.getElementById('status-announcement');
    if (el) el.textContent = text;
  }

  // ── Interacción ───────────────────────────────────────────────────────────

  onClick(e) {
    const s = this.state;
    if (!s) return;

    const chip = e.target.closest('[data-chip]');
    if (chip) { this.selectChip(Number(chip.dataset.chip)); return; }

    const card = e.target.closest('.bdef-ps__card');
    if (card) {
      const nodes = this.currentNodes();
      const n = nodes.find(x => x.handle === card.dataset.handle);
      if (n) {
        this.track('select_item', {
          item_list_id: 'predictive_search',
          item_list_name: `búsqueda: ${s.term}`,
          currency: n.priceRange.minVariantPrice.currencyCode || CFG.CURRENCY,
          items: [{
            item_id: n.handle,
            item_name: n.title,
            item_brand: n.vendor,
            item_category: n.productType,
            index: Number(card.dataset.pos),
            price: parseFloat(n.priceRange.minVariantPrice.amount) || 0
          }],
          transport_type: 'beacon'
        });
      }
      return;
    }

    if (e.target.closest('[data-viewall]')) {
      this.track('view_search_results', { search_term: s.term, results_shown: s.total, transport_type: 'beacon' });
      return;
    }

    const banner = e.target.closest('[data-banner]');
    if (banner) {
      this.track('search_smart_link', { search_query: s.term, link_label: banner.textContent.trim(), link_url: banner.getAttribute('href'), transport_type: 'beacon' });
      return;
    }

    const col = e.target.closest('[data-collection]');
    if (col) this.track('search_collection_click', { search_query: s.term, collection: col.dataset.collection, transport_type: 'beacon' });
  }

  currentNodes() {
    const s = this.state;
    const chip = s.r.chips[s.active];
    return (chip && chip.nodes && chip.nodes.length) ? chip.nodes : (s.r.exactNodes.length ? s.r.exactNodes : s.r.broadNodes);
  }

  async selectChip(i) {
    const s = this.state;
    const chip = s.r.chips[i];
    if (!chip || i === s.active) return;
    const grid = this.slotEl.querySelector('[data-grid]');
    this.slotEl.querySelectorAll('[data-chip]').forEach(b => b.setAttribute('aria-pressed', String(Number(b.dataset.chip) === i)));
    s.active = i;
    this.track('search_chip', { search_term: s.term, chip: chip.term.label });

    if (!chip.nodes) {
      grid?.setAttribute('aria-busy', 'true');
      try {
        const data = await gql({
          query: `query BdefChip($q: String!) { search(query: $q, first: ${CFG.EXACT_LIMIT}, types: PRODUCT, unavailableProducts: HIDE) { totalCount nodes { ... on Product { ${PRODUCT_FIELDS} } } } }`,
          variables: { q: chip.query }
        });
        chip.nodes = (data.search?.nodes || []).filter(n => n.featuredImage);
      } catch (e) {
        console.error('[bdef-search] chip', e);
        chip.nodes = [];
      }
      grid?.removeAttribute('aria-busy');
      if (this.state !== s || s.active !== i) return;
    }

    if (grid) grid.innerHTML = this.cardsHtml(chip.nodes, s.term);
    s.viewAll = this.viewAllFor(s.p, chip.term);
    s.total = chip.count;
    const more = this.slotEl.querySelector('.bdef-ps__more');
    if (more) more.outerHTML = this.viewAllHtml(s.viewAll);
    this.announce(`${chip.count} ${chip.term.label.toLowerCase()}`);
  }

  // ── GA4 ───────────────────────────────────────────────────────────────────

  track(name, params) {
    if (typeof window.gtag === 'function') window.gtag('event', name, params);
    else (window.dataLayer = window.dataLayer || []).push({ event: name, ...params });
  }

  /** Un evento search por término cuando el usuario deja de escribir, no uno por pulsación. */
  scheduleTrack(term, p, count, type) {
    clearTimeout(this.trackTimer);
    this.trackTimer = setTimeout(() => {
      if (term === this.lastTracked) return;
      this.lastTracked = term;
      const gender = p.genero?.label || 'ninguno';
      this.track('search', { search_term: term, search_results_count: count, search_gender_filter: gender, search_type: type });
      if (!count) this.track('search_no_results', { search_query: term, search_gender_filter: gender });
    }, CFG.TRACK_IDLE_MS);
  }
}

if (!window.customElements.get('predictive-search')) {
  window.customElements.define('predictive-search', PredictiveSearch);
}
